import ms from 'ms';

import {
    type CachedRecentSearch,
    deleteRecentSearch as deleteRecentSearchApi,
    getRecentSearches,
    isValidRecentSearch,
    postRecentSearch,
} from '@/redux/api/saveSearch';
import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getBidderId, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';
import { GlobalState } from '@/redux/store';
import {
    deleteRecentSearchFromCookie,
    getRecentSearchesFromCookie,
    saveRecentSearchToCookie,
} from '@/utils/recentSearchCookie';

import {
    getRecentSearchKeywordFromParams,
    isKeywordInRecentSearches,
    SaveSearchParamsOrKey,
} from './saveSearch.helpers';

const HEADER_RECENT_SEARCHES_COUNT = 10;
const HEADER_RECENT_SEARCH_LOTS_PER_SEARCH = 1;
const RECENT_SEARCHES_CACHE_TIME = ms('5m');

const shouldFetchRecentSearches = (state: GlobalState, force: boolean) => {
    if (force) {
        return true;
    }

    if (state.saveSearch.recentSearchesLoading) {
        return false;
    }

    const recentSearchesLoaded = state.saveSearch.recentSearchesLoaded;

    if (!recentSearchesLoaded) {
        return true;
    }

    return Date.now() - recentSearchesLoaded >= RECENT_SEARCHES_CACHE_TIME;
};

export type SubmitRecentSearchParams = {
    saveSearchParams: SaveSearchParamsOrKey;
    url: string;
};

export const submitRecentSearch = createAsyncThunk<CachedRecentSearch | null, SubmitRecentSearchParams>(
    'la/domain/saveSearch/submitRecentSearch',
    async ({ saveSearchParams, url }, { getState }) => {
        const state = getState();

        if (isUserLoggedIn(state)) {
            const { payload } = await postRecentSearch({
                authToken: getAuthToken(state),
                deployment: getDeployment(state),
                saveSearchParams,
                url,
            });

            return isValidRecentSearch(payload) ? payload : null;
        }

        const keyword = getRecentSearchKeywordFromParams(saveSearchParams);
        const item: CachedRecentSearch = {
            facets: { keyword },
            hash: url,
            url,
        };
        saveRecentSearchToCookie(item);
        return item;
    },
    {
        condition: ({ saveSearchParams }, { getState }) => {
            const state = getState();
            const keyword = getRecentSearchKeywordFromParams(saveSearchParams);
            return Boolean(keyword) && !isKeywordInRecentSearches(state, keyword);
        },
    }
);

export const deleteRecentSearch = createAsyncThunk<string, { hash: string }>(
    'la/domain/saveSearch/deleteRecentSearch',
    async ({ hash }, { getState }) => {
        const state = getState();

        if (isUserLoggedIn(state)) {
            await deleteRecentSearchApi({
                authToken: getAuthToken(state),
                deployment: getDeployment(state),
                hash,
            });
        } else {
            deleteRecentSearchFromCookie(hash);
        }

        return hash;
    }
);

type FetchRecentSearchesArgs = { force?: boolean };

export const fetchRecentSearches = createAsyncThunk<CachedRecentSearch[], FetchRecentSearchesArgs>(
    'la/domain/saveSearch/fetchRecentSearches',
    async (_, { getState }) => {
        const state = getState();

        if (!isUserLoggedIn(state)) {
            return getRecentSearchesFromCookie();
        }

        const response = await getRecentSearches({
            authToken: getAuthToken(state),
            bidderId: getBidderId(state),
            deployment: getDeployment(state),
            numLotsPerRecentSearch: HEADER_RECENT_SEARCH_LOTS_PER_SEARCH,
            numRecentSearchesToReturn: HEADER_RECENT_SEARCHES_COUNT,
        });

        return (response.payload ?? []).filter(isValidRecentSearch);
    },
    {
        condition: ({ force = false }, { getState }) => {
            const state = getState();
            return shouldFetchRecentSearches(state, force);
        },
    }
);
